import React, { useState } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import Button from "@mui/material/Button";
import { CircularProgress } from "@mui/material";
import NotificationSnackbar from "./NotificationBar";
import { useNavigate } from "react-router-dom";

function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [usernameError, setUsernameError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const navigate = useNavigate();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  const handleSnackbarOpen = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setUsernameError(false);
    setPasswordError(false);
    let valid = true;

    if (!username.trim()) {
      setUsernameError(true);
      valid = false;
    }
    if (!password.trim()) {
      setPasswordError(true);
      valid = false;
    }
    if (!valid) return;

    setLoading(true);
    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: username, password }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Login failed");

      localStorage.setItem("isLoggedIn", "true");
      localStorage.setItem("userToken", data.token);
      localStorage.setItem("userData", JSON.stringify(data.user));
      handleSnackbarOpen("Login successful!", "success");

      const role = data.user.roleId?.toUpperCase();
      setTimeout(() => {
        switch (role) {
          case "SUPPORT_AGENT":
            navigate("/AssignedTickets");
            break;
          case "APPLICANT":
          case "EMPLOYEE":
          case "NO_ROLE":
            navigate("/RespondedTickets");
            break;
          default:
            navigate("/PendingTickets");
        }
      }, 1000);
    } catch (error) {
      console.error("Login error:", error);
      handleSnackbarOpen(error?.message, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
  sx={{
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    bgcolor: "#232324ff", // optional background color for better contrast
  }}
>

      <Box
        sx={{
          p: 4,
          border: "1px solid #fff",
          borderRadius: 2,
          maxWidth: 400,
          bgcolor: "#fff",
        }}
      >
        <Typography
          gutterBottom
          sx={{
            fontWeight: 600,
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
            color: "text.primary",
            mb: 3,
            textAlign: "center",
            position: "relative",
            "&:after": {
              content: '""',
              display: "block",
              width: "50px",
              height: "3px",
              background: "linear-gradient(to right, #3b82f6, #1e40af)",
              margin: "8px auto 0",
              borderRadius: "3px",
            },
          }}
        >
          Login
        </Typography>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Username"
            size="medium"
            margin="dense"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            error={usernameError}
            helperText={usernameError ? "Username is required" : ""}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                "& fieldset": {
                  borderWidth: 1.5,
                },
                "&:hover fieldset": {
                  borderColor: "primary.main",
                },
              },
            }}
          />
          <TextField
            fullWidth
            label="Password"
            type={showPassword ? "text" : "password"}
            size="medium"
            margin="dense"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={passwordError}
            helperText={passwordError ? "Password is required" : ""}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                "& fieldset": {
                  borderWidth: 1.5,
                },
                "&:hover fieldset": {
                  borderColor: "primary.main",
                },
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleTogglePassword} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={loading}
            sx={{
              mt: 1,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              letterSpacing: 0.5,
              textTransform: "none",
              background: "linear-gradient(to right, #3b82f6, #1e40af)",
              boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
              "&:hover": {
                background: "linear-gradient(to right, #2563eb, #1d4ed8)",
                boxShadow: "0 6px 18px rgba(37, 99, 235, 0.4)",
              },
              transition: "all 0.2s ease",
            }}
          >
            {loading ? <CircularProgress size={24} color="white" /> : "Submit"}
          </Button>
        </form>
      </Box>
      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Box>
  );
}

export default Login;
